import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { concat, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { getOwner } from '@ember/owner';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { task } from 'ember-concurrency';
import { variation } from 'ember-launch-darkly';
import { gt, and, eq, not } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { lowercase } from 'ember-cli-string-helpers';
import { HashicorpCloudIamSSOType } from '@clients/cloud-iam';
import {
  HdsPageHeader,
  HdsDropdown,
  HdsAlert,
  HdsIcon,
  HdsModal,
  HdsButtonSet,
  HdsButton,
  HdsLinkInline,
  HdsLinkStandalone,
  HdsTextBody,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  FlexGrid,
  DefinitionList,
  Flex,
  Time,
  ZeroState,
  CoreModalConfirm,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import { supportTicketPrefillHref } from 'core/utils/support-ticket';
import DefaultRoleAdmin from 'hcp/components/sso/banner/default-role-admin';
import Banner from 'hcp/components/scim/banner';
import equalDates from 'hcp/helpers/equal-dates';

/**
 *
 * `PageOrganizationsDetailSso` displays configuration details (if enabled) for SSO settings.
 *
 *
 * ```
 * <Page::Organizations::Detail::Sso
 *   @model={{this.model}}
 * />
 * ```
 *
 * @class PageOrganizationsDetailSso
 *
 */
export default class PageOrganizationsDetailSso extends Component {
  @service abilities;
  @service api;
  @service flashMessages;
  @service intl;

  @service router;
  @service snapshots;
  @service userContext;

  @tracked isModalActive = false;
  @tracked modalList = [
    'components.page.organizations.sso.delete-modal.list.1',
    'components.page.organizations.sso.delete-modal.list.2',
    'components.page.organizations.sso.delete-modal.list.3',
  ];

  get supportTicketHref() {
    return supportTicketPrefillHref(this.userContext);
  }

  get scimEnabled() {
    const { isEnabled } = this.args.model;
    const scimFeatureFlagEnabled = variation('hcp-identity-scim-enabled');
    return scimFeatureFlagEnabled && isEnabled;
  }

  get showScimBanner() {
    const { isEnabled, ssoType } = this.args.model ?? {};
    // Permissions
    const canEnableScim = this.abilities.can('enable scim');
    const canGetScim = this.abilities.can('get scim');
    const canListScimTokens = this.abilities.can('list scim-token');
    // Conditions
    const scimNotEnabled = !isEnabled;
    const isSaml = ssoType === HashicorpCloudIamSSOType.SAML;
    // Show the SCIM banner only if SCIM is not enabled, SSO is SAML,
    // and the user has appropriate permissions to enable SCIM and visit the SCIM route.
    return (
      scimNotEnabled &&
      isSaml &&
      canEnableScim &&
      canGetScim &&
      canListScimTokens
    );
  }

  toggleDeleteModal = () => {
    this.isModalActive = !this.isModalActive;
  };

  save = task(async () => {
    let organizationId = this.args.model.org.id;

    let { type } =
      await this.api.sso.sSOManagementServiceGetSSOType(organizationId);

    try {
      if (type === HashicorpCloudIamSSOType.SAML) {
        await this.api.sso.sSOManagementServiceDeleteSSOConfiguration(
          organizationId,
          type
        );
      } else {
        await this.api.authConfig.authConfigServiceDeleteAuthConnectionFromOrganization(
          organizationId
        );
      }
    } catch (err) {
      if (macroCondition(isDevelopingApp())) {
        console.log(`Error while removing ${type} configuration\n`, err);
      }
      this.isModalActive = false;
      this.flashMessages.error(
        this.intl.t(
          'components.page.organizations.sso.delete-modal.sso-deletion-failed'
        ),
        {
          content: this.intl.t(
            'components.page.organizations.sso.delete-modal.something-went-wrong',
            { orgName: this.args.model.org.name, htmlSafe: true }
          ),
          linkIcon: 'help',
          linkIconPosition: 'leading',
          linkText: this.intl.t(
            'components.page.organizations.sso.delete-modal.contact-support'
          ),
          linkUrl: this.supportTicketHref,
        }
      );
      return;
    }
    this.isModalActive = false;

    this.refreshRoute();

    this.flashMessages.success(
      this.intl.t(
        'components.page.organizations.sso.delete-modal.success-message',
        {
          orgName: this.args.model.org.name,
        }
      )
    );
  });

  refreshRoute = () => {
    let route = getOwner(this).lookup(`route:cloud.orgs.detail.settings.sso`);
    return route.refresh();
  };

  <template>
    {{#let
      (gt @model.configs.length 0)
      (and
        (eq @model.defaultRole "roles/admin")
        (variation "hcp-identity-sso-default-role")
      )
      as |hasSSOSetup isdefaultSsoRoleAdmin|
    }}
      <div data-test-sso>
        {{#if isdefaultSsoRoleAdmin}}
          <DefaultRoleAdmin @ssoType={{@model.ssoType}} />
        {{/if}}

        <CoreLayoutMain>
          <:header>
            <HdsPageHeader as |PH|>
              <PH.Breadcrumb>
                <BreadcrumbsContainer />
              </PH.Breadcrumb>
              <PH.Title data-test-title>
                {{t "components.page.organizations.sso.detail.header"}}
              </PH.Title>
              <PH.IconTile @icon="cloud-lock" />
              {{#if hasSSOSetup}}
                <PH.Actions>
                  <HdsDropdown @listPosition="bottom-right" as |dd|>
                    <dd.ToggleButton
                      @text={{t "components.form.manage"}}
                      @color="secondary"
                      data-test-menu
                    />
                    {{! we must dynamically render the route based on the connection type once API endpoints available}}
                    <dd.Interactive
                      @model={{@model.org.id}}
                      @route={{concat
                        "cloud.orgs.detail.settings.sso."
                        (lowercase @model.ssoType)
                        ".configure"
                      }}
                      @text={{t
                        "components.page.organizations.sso.detail.manage.edit-connection"
                      }}
                      data-test-edit-config-link
                    />
                    <dd.Interactive
                      @text={{t
                        "components.page.organizations.sso.detail.manage.delete-connection"
                      }}
                      {{on "click" this.toggleDeleteModal}}
                      data-test-delete-config
                    />
                  </HdsDropdown>
                </PH.Actions>
              {{/if}}
            </HdsPageHeader>
          </:header>

          <:body>
            <section class="sso">
              {{#if hasSSOSetup}}
                <FlexGrid as |Row|>
                  <Row.Item @xs="12" @md="8" {{boxPadding "0 0 md 0"}}>
                    <HdsAlert
                      @type="inline"
                      data-test-sso-recovery-alert
                      as |A|
                    >
                      <A.Title>
                        {{t
                          "components.page.organizations.sso.detail.sso-recovery.title"
                        }}
                      </A.Title>
                      <A.Description>
                        {{t
                          "components.page.organizations.sso.detail.sso-recovery.description"
                        }}
                      </A.Description>
                      <A.LinkStandalone
                        @icon="arrow-right"
                        @iconPosition="trailing"
                        @text={{t
                          "components.page.organizations.sso.detail.sso-recovery.link.text"
                        }}
                        @href={{t
                          "components.page.organizations.sso.detail.sso-recovery.link.href"
                        }}
                      />
                    </HdsAlert>
                  </Row.Item>
                  <Row.Item @xs="12" @md="8">
                    <DefinitionList {{boxPadding "md 0 xl 0"}} as |DL|>
                      <DL.Key>
                        {{t "components.page.organizations.sso.detail.status"}}
                      </DL.Key>
                      <DL.Value data-test-sso-status>
                        <Flex @alignItems="center" @gap="2xs">
                          <HdsIcon
                            @name="check-circle-fill"
                            @color="green"
                            @isInline={{true}}
                          />
                          <span>
                            {{t
                              "components.page.organizations.sso.detail.enabled"
                            }}
                          </span>
                        </Flex>
                      </DL.Value>
                      <DL.Key>
                        {{t
                          "components.page.organizations.sso.detail.identity-protocol"
                        }}
                      </DL.Key>
                      <DL.Value data-test-sso-type>
                        {{@model.ssoType}}
                      </DL.Value>
                      <DL.Key>
                        {{t
                          "components.page.organizations.sso.detail.creation-date"
                        }}
                      </DL.Key>
                      <DL.Value data-test-created-at>
                        <Time
                          @date={{get @model.configs "0.createdAt"}}
                          @display="friendly-only"
                        />
                      </DL.Value>
                      {{#if
                        (not
                          (equalDates
                            (get @model.configs "0.createdAt")
                            (get @model.configs "0.updatedAt")
                          )
                        )
                      }}
                        <DL.Key>
                          {{t
                            "components.page.organizations.sso.detail.updated-on"
                          }}
                        </DL.Key>
                        <DL.Value data-test-updated-on>
                          <Time
                            @date={{get @model.configs "0.updatedAt"}}
                            @display="friendly-only"
                          />
                        </DL.Value>
                      {{/if}}
                      <DL.Key>
                        {{t
                          "components.page.organizations.sso.detail.email-domains"
                        }}
                      </DL.Key>
                      <DL.Value data-test-email-domains>
                        {{#if (eq @model.ssoType "SAML")}}
                          {{#each
                            (get @model.configs "0.saml.emailDomains")
                            as |emailDomain index|
                          }}
                            {{#if (eq index @model.lastDomainIndex)}}
                              <span data-test-domain={{index}}>
                                {{emailDomain}}
                              </span>
                            {{else}}
                              <span data-test-domain={{index}}>
                                {{!-- prettier-ignore --}}
                                {{emailDomain}},&nbsp;
                              </span>
                            {{/if}}
                          {{/each}}
                        {{else}}
                          {{get @model.configs "0.emailDomain"}}
                        {{/if}}
                      </DL.Value>
                      {{#if (variation "hcp-identity-sso-default-role")}}
                        <DL.Key data-test-sso-default-role-key>
                          {{t
                            "components.page.organizations.sso.detail.default-role.key"
                          }}
                        </DL.Key>
                        <DL.Value data-test-sso-default-role-value>
                          {{t
                            (concat
                              "sso.default-role-display."
                              (get @model.configs "0.defaultRole")
                            )
                          }}
                          {{! template-lint-disable no-whitespace-for-layout }}
                          &nbsp;&nbsp;&nbsp;&nbsp;
                          <HdsLinkInline
                            data-test-sso-default-role-edit-link
                            @icon="arrow-right"
                            @iconPosition="trailing"
                            @model={{@model.org.id}}
                            @route={{concat
                              "cloud.orgs.detail.settings.sso."
                              (lowercase @model.ssoType)
                              ".configure"
                            }}
                          >
                            {{t
                              "components.page.organizations.sso.detail.default-role.edit-link.text"
                            ~}}
                          </HdsLinkInline>
                        </DL.Value>
                      {{/if}}
                    </DefinitionList>
                  </Row.Item>
                  {{#if this.showScimBanner}}
                    <Row.Item @xs="12" @sm="8" @md="5">
                      <Banner />
                    </Row.Item>
                  {{/if}}
                </FlexGrid>
              {{else}}
                <ZeroState as |Z|>
                  <Z.Header>
                    {{t
                      "components.page.organizations.sso.configure.empty-state.title"
                    }}
                  </Z.Header>
                  <Z.Message>
                    {{t
                      "components.page.organizations.sso.configure.empty-state.body"
                      htmlSafe=true
                    }}
                    {{#if (eq @model.org.tfcSynced false)}}
                      <HdsAlert
                        {{boxMargin "md 0 0 0"}}
                        data-test-setup-sso-tfc-synced-org-alert
                        @type="compact"
                        @color="warning"
                        as |A|
                      >
                        <A.Description>
                          {{t
                            "components.page.organizations.sso.configure.empty-state.tfc-synced-alert"
                          }}
                        </A.Description>
                      </HdsAlert>
                    {{/if}}
                  </Z.Message>
                  <Z.Action>
                    <HdsLinkStandalone
                      @icon="arrow-right"
                      @iconPosition="trailing"
                      @model={{@model.org.id}}
                      @route="cloud.orgs.detail.settings.sso.select-method"
                      @text={{t
                        "components.page.organizations.sso.configure.empty-state.footer"
                      }}
                      data-test-setup-sso
                    />
                  </Z.Action>
                </ZeroState>
              {{/if}}
            </section>
          </:body>
        </CoreLayoutMain>
        {{#if this.isModalActive}}
          {{#if this.scimEnabled}}
            <HdsModal
              @color="warning"
              @onClose={{this.toggleDeleteModal}}
              as |M|
            >
              <M.Header
                @icon="alert-triangle"
                data-test-sso-scim-enabled-modal-header
              >
                {{t
                  "components.page.organizations.sso.scim-enabled-modal.title"
                }}
              </M.Header>
              <M.Body>
                <HdsTextBody
                  @color="primary"
                  @tag="p"
                  data-test-sso-scim-enabled-modal-body
                >
                  {{t
                    "components.page.organizations.sso.scim-enabled-modal.description"
                  }}
                </HdsTextBody>
              </M.Body>
              <M.Footer as |F|>
                <HdsButtonSet>
                  <HdsButton
                    @icon="arrow-right"
                    @iconPosition="trailing"
                    @route="cloud.orgs.detail.settings.scim"
                    @text={{t
                      "components.page.organizations.sso.scim-enabled-modal.view-scim-provisioning"
                    }}
                    data-test-sso-scim-enabled-modal-button
                  />
                  <HdsButton
                    @color="secondary"
                    @text={{t
                      "components.page.organizations.sso.scim-enabled-modal.cancel"
                    }}
                    {{on "click" F.close}}
                  />
                </HdsButtonSet>
              </M.Footer>
            </HdsModal>
          {{else}}
            <CoreModalConfirm
              @confirmHelp={{t
                "components.page.organizations.sso.delete-modal.delete-confirmation.confirm-help"
                htmlSafe=true
              }}
              @confirmLabel={{t
                "components.page.organizations.sso.delete-modal.delete-confirmation.confirm-label"
              }}
              @confirmText={{t "components.core.modal.confirm.delete"}}
              @headerIcon="alert-diamond"
              @headerText={{t
                "components.page.organizations.sso.delete-modal.header"
              }}
              @onCancel={{this.toggleDeleteModal}}
              @onSubmit={{this.save}}
            >
              <:body>
                <HdsTextBody
                  @color="primary"
                  @tag="div"
                  {{boxPadding "0 0 lg 0"}}
                >
                  <span>
                    {{t
                      "components.page.organizations.sso.delete-modal.description.1"
                    }}
                  </span>
                  <ol>
                    {{#each this.modalList as |item|}}
                      <li>
                        {{t
                          item
                          htmlSafe=true
                          orgName=@model.org.name
                          ssoType=@model.ssoType
                        }}
                      </li>
                    {{/each}}
                  </ol>
                  <span>
                    {{t
                      "components.page.organizations.sso.delete-modal.description.2"
                      htmlSafe=true
                    }}
                  </span>
                </HdsTextBody>
              </:body>
            </CoreModalConfirm>
          {{/if}}
        {{/if}}
      </div>
    {{/let}}
  </template>
}
